import React from "react";
import { Link } from "react-router-dom";
import { HERO_BG, WHCO_LOGO } from "../shared";

const PILLAR_ART_BASE = "/api/img-proxy/job_4a9c9726-b923-42d1-a9c6-eeea4f9a7474/artifacts";
const PILLARS_HERO = `${PILLAR_ART_BASE}/c9ykufey_pillars-hero.png`;
const PILLAR_SLIDES = [
  { key: "worship",   src: `${PILLAR_ART_BASE}/78wwem06_pillar-worship.png`,   alt: "W = Worship",
    aria: "Worship pillar — learn more" },
  { key: "holiness",  src: `${PILLAR_ART_BASE}/t7c792q7_pillar-holiness.png`,  alt: "H = Holiness",
    aria: "Holiness pillar — learn more" },
  { key: "covenant",  src: `${PILLAR_ART_BASE}/wtlcqq8j_pillar-covenant.png`,  alt: "C = Covenant",
    aria: "Covenant pillar — learn more" },
  { key: "obedience", src: `${PILLAR_ART_BASE}/m6r55gw6_pillar-obedience.png`, alt: "O = Obedience",
    aria: "Obedience pillar — learn more" },
];

const WhoWeArePage = () => {
  const threefold = [
    { title: "Making Disciples", body: "We are committed to helping every believer follow Jesus with their whole heart — grounded in the Word, walking in the Spirit, and shaped by community. Discipleship is not an event; it is a lifestyle of surrender, growth, and multiplication." },
    { title: "Raising Leaders", body: "We identify, mentor, and equip men and women to walk in their God-given calling. Through Kingdom Academy, Leadership Development, and hands-on ministry, we release leaders who serve with integrity, humility, and Kingdom authority." },
    { title: "Sending to the Nations", body: "We are a launching pad. From Lancaster to Tijuana, Brazil, Africa, and beyond, we send disciples and leaders into every sphere of influence to preach the Gospel, plant churches, and disciple nations." },
  ];

  return (
    <div className="pt-20 fade-in">
      <section className="relative min-h-[72vh] flex items-center justify-center bg-black text-white overflow-hidden" data-testid="who-hero">
        <div className="absolute inset-0 bg-cover bg-center opacity-40" style={{ backgroundImage: `url('${HERO_BG.about}')` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/60 to-black" />
        <div className="relative z-10 text-center px-8 max-w-4xl mx-auto">
          <img src={WHCO_LOGO} alt="World Harvest Christian Outreach" className="h-20 md:h-24 w-auto mx-auto mb-10 opacity-95" data-testid="who-hero-logo" />
          <p className="heading-caps text-xs text-white/50 mb-6 tracking-[0.3em]">Our Identity</p>
          <h1 className="text-5xl md:text-7xl lg:text-8xl leading-tight" style={{ fontFamily: "Playfair Display, serif", fontWeight: 500 }} data-testid="who-hero-title">
            Who We <span className="italic">Are</span>
          </h1>
          <p className="mt-8 text-lg md:text-xl text-white/70 font-light max-w-2xl mx-auto">
            Making Disciples. Raising Up Leaders. Launching to the Nations.
          </p>
        </div>
      </section>

      <section className="py-32 bg-white" data-testid="who-pillars-section">
        <div className="max-w-7xl mx-auto px-8">
          <div className="max-w-3xl mb-16">
            <p className="heading-caps text-xs text-black/40 mb-4 tracking-[0.2em]">Core Pillars</p>
            <h2 className="text-4xl md:text-5xl lg:text-6xl leading-tight" style={{ fontFamily: "Playfair Display, serif" }}>
              The four values that <span className="italic">shape everything</span> we do.
            </h2>
          </div>

          <div className="mb-10 border border-black/10 shadow-lg overflow-hidden bg-white" data-testid="pillars-hero-banner">
            <img
              src={PILLARS_HERO}
              alt="WHCO Four Core Pillars — Living in Kingdom Culture"
              loading="lazy"
              sizes="(max-width: 768px) 100vw, (max-width: 1280px) 90vw, 1280px"
              className="block w-full h-auto"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="pillars-grid">
            {PILLAR_SLIDES.map((slide) => (
              <Link
                key={slide.key}
                to={`/pillars/${slide.key}`}
                aria-label={slide.aria}
                data-testid={`pillar-slide-${slide.key}`}
                className="group block border border-black/10 shadow-lg overflow-hidden bg-white transition-all duration-500 hover:shadow-2xl hover:-translate-y-1 hover:border-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-700 focus-visible:ring-offset-2"
              >
                <img
                  src={slide.src}
                  alt={slide.alt}
                  loading="lazy"
                  sizes="(max-width: 768px) 100vw, (max-width: 1280px) 45vw, 620px"
                  className="block w-full h-auto transition-transform duration-500 group-hover:scale-[1.02]"
                />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-32 bg-neutral-50" data-testid="who-mission">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <div>
              <p className="heading-caps text-xs text-black/40 mb-4 tracking-[0.2em]">Our Mission</p>
              <h2 className="text-4xl md:text-5xl lg:text-6xl mb-8 leading-tight" style={{ fontFamily: "Playfair Display, serif" }}>
                To exalt the name of <span className="italic">Jesus</span>.
              </h2>
            </div>
            <div>
              <p className="text-lg text-black/70 leading-relaxed mb-6">
                World Harvest Christian Outreach exists to make disciples of Jesus Christ, raise up
                Kingdom leaders, and send them into the nations with the Gospel — carrying the
                presence, power, and love of God everywhere we go.
              </p>
              <p className="text-black/60 leading-relaxed">
                We believe every person we encounter is loved by God and created with purpose. Our
                mission is to see them encounter Jesus, become fully surrendered followers, and step
                into the life He designed for them.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-32 bg-black text-white" data-testid="who-vision">
        <div className="max-w-5xl mx-auto px-8 text-center">
          <p className="heading-caps text-xs text-white/40 mb-6 tracking-[0.3em]">Our Vision</p>
          <h2 className="text-4xl md:text-5xl lg:text-6xl mb-10 leading-tight" style={{ fontFamily: "Playfair Display, serif" }}>
            A generation of revivalists <span className="italic">carrying His glory</span> into every sphere.
          </h2>
          <p className="text-lg text-white/70 leading-relaxed max-w-3xl mx-auto">
            We envision a Kingdom-minded, prophetic, and apostolic community that pursues God&apos;s
            presence above all else — a house of prayer and reformation that touches Lancaster,
            transforms cities, and launches missionaries to the ends of the earth.
          </p>
        </div>
      </section>

      <section className="py-32 bg-white" data-testid="who-faith">
        <div className="max-w-2xl mx-auto px-8 text-center">
          <p className="heading-caps text-xs text-black/40 mb-4 tracking-[0.2em]">What we believe</p>
          <h2 className="text-4xl md:text-5xl mb-16 leading-tight" style={{ fontFamily: "Playfair Display, serif" }}>
            Our Faith <span className="italic">Statement</span>.
          </h2>
          <div className="space-y-10 text-lg md:text-xl text-black/75 leading-relaxed" data-testid="creed">
            {[
              "the Bible is the inspired, infallible, and authoritative Word of God — the final authority for faith and life.",
              "in one God, eternally existing in three persons: Father, Son, and Holy Spirit.",
              "in the deity of Jesus Christ — His virgin birth, sinless life, atoning death, bodily resurrection, ascension, and personal return in power and glory.",
              "salvation is by grace through faith in Jesus Christ alone — a free gift received by all who repent and believe.",
              "in the present-day ministry of the Holy Spirit, who indwells and empowers every believer to live godly, exercise spiritual gifts, and bear witness to Christ.",
              "in the priesthood of every believer, the unity of the Body of Christ, and the Great Commission — to make disciples of every nation.",
              "in the resurrection of the saved and the lost — the saved to eternal life with God, and the lost to eternal separation from Him.",
            ].map((rest, i, arr) => (
              <React.Fragment key={i}>
                <p data-testid={`creed-${i}`}>
                  <strong className="font-semibold text-black">We believe</strong> {rest}
                </p>
                {i < arr.length - 1 && (
                  <div className="flex justify-center" aria-hidden="true">
                    <span className="inline-block h-1 w-1 rounded-full bg-black/25" />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      <section className="py-32 bg-black text-white" data-testid="who-threefold">
        <div className="max-w-7xl mx-auto px-8">
          <div className="max-w-3xl mb-20">
            <p className="heading-caps text-xs text-white/40 mb-4 tracking-[0.2em]">Our Threefold Call</p>
            <h2 className="text-4xl md:text-5xl lg:text-6xl leading-tight" style={{ fontFamily: "Playfair Display, serif" }}>
              How we live it <span className="italic">out</span>.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-10">
            {threefold.map((t, i) => (
              <div key={t.title} data-testid={`threefold-${i}`}>
                <div className="text-red-500/80 text-sm mb-4 tracking-[0.25em] uppercase" style={{ fontFamily: "Playfair Display, serif" }}>0{i + 1}</div>
                <h3 className="text-2xl md:text-3xl mb-6 leading-snug" style={{ fontFamily: "Playfair Display, serif" }}>{t.title}</h3>
                <p className="text-white/60 leading-relaxed text-sm">{t.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-32 bg-white text-center" data-testid="who-cta">
        <div className="max-w-3xl mx-auto px-8">
          <p className="heading-caps text-xs text-black/40 mb-6 tracking-[0.3em]">Join Us</p>
          <h2 className="text-4xl md:text-5xl lg:text-6xl mb-8 leading-tight" style={{ fontFamily: "Playfair Display, serif" }}>
            There&apos;s a place for you <span className="italic">here</span>.
          </h2>
          <p className="text-lg text-black/60 leading-relaxed mb-12 max-w-xl mx-auto">
            Whether you&apos;re exploring faith, looking for a church home, or ready to step into what God is doing at WHCO — we&apos;d love to meet you.
          </p>
          <Link to="/contact?plan=1" className="btn-primary inline-block" data-testid="who-get-connected">Get Connected</Link>
        </div>
      </section>
    </div>
  );
};

export default WhoWeArePage;
